/**
 * <gravity-section> — a lightweight 2D rigid-body playground.
 *
 * Children marked with [data-gravity-item] drop from above the section,
 * collide with each other and the section bounds, and settle into a pile.
 * Items stay real DOM (links/buttons remain clickable, editor-selectable);
 * the engine only drives their transforms.
 *
 * Attributes on <gravity-section>:
 *   data-gravity      gravity multiplier (default 1)
 *   data-restitution  bounciness 0–0.9 (default 0.4)
 *   data-stagger      ms between drops (default 120)
 *   data-drag         present = items are draggable/throwable
 *
 * Attributes on items:
 *   data-shape="circle" | "pill"   collider hint (default circle)
 *   data-x="0..100"                preferred horizontal drop position (%)
 *   data-pinned + data-y/data-rotation             pin on desktop
 *   data-pinned-mobile + data-*-mobile variants    pin on mobile
 *
 * Items hidden via CSS (e.g. display:none on mobile) are excluded from the
 * simulation; crossing the 750px breakpoint re-collects bodies so mobile
 * pins/hides apply without a reload.
 *
 * Bodies use circle colliders (pills use an averaged radius and damped,
 * clamped rotation so buttons never end up upside down).
 */

const BASE_GRAVITY = 2600; // px/s²
const MAX_DT = 1 / 30;
const SUBSTEPS = 3;
const SOLVER_ITERATIONS = 4;
const SLEEP_SPEED = 10; // px/s
const SLEEP_FRAMES = 40;
const DRAG_THRESHOLD = 6; // px before a press becomes a drag

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const mobileMedia = window.matchMedia('(max-width: 749px)');

class GravitySection extends HTMLElement {
  connectedCallback() {
    this.bodies = [];
    this.staticMode = false;
    this.engineInited = false;

    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    mobileMedia.addEventListener('change', this.onBreakpointChange);

    // All-pinned mobile layouts are positioned by CSS alone (see the
    // .gravity-fall--static-mobile rules); the physics engine and all its
    // observers never boot, so the section costs nothing on mobile.
    if (this.isStaticLayout()) {
      this.staticMode = true;
      return;
    }
    this.initEngine();
  }

  // On mobile, a layout where every visible item is pinned has nothing to
  // simulate. (Hidden items have no offsetParent; an empty section counts.)
  isStaticLayout() {
    if (!mobileMedia.matches) return false;
    const items = [...this.querySelectorAll('[data-gravity-item]')].filter(
      (el) => el.offsetParent !== null
    );
    return items.every((el) => el.hasAttribute('data-pinned-mobile'));
  }

  // Skip the drop choreography and render the pile pre-settled when the
  // visitor prefers reduced motion, or when the merchant enabled "Skip drop
  // animation in editor" (data-static-editor, only present in design mode).
  get skipAnimation() {
    return reducedMotion.matches || this.hasAttribute('data-static-editor');
  }

  initEngine() {
    this.engineInited = true;
    this.rafId = 0;
    this.running = false;
    this.started = false;
    this.lastTime = 0;
    this.clock = 0;
    this.dragBody = null;
    this.suppressClick = false;

    this.gravity = parseFloat(this.dataset.gravity || '1') * BASE_GRAVITY;
    this.restitution = Math.min(parseFloat(this.dataset.restitution || '0.4'), 0.9);
    this.stagger = parseFloat(this.dataset.stagger || '120') / 1000;
    this.dragEnabled = this.hasAttribute('data-drag');
    this.squashEnabled = !this.hasAttribute('data-no-squash');
    // sinks the physics floor below the visible bottom edge so the
    // lowest items straddle the clip and look cut off by the section
    this.floorOffset = parseFloat(this.dataset.floorOffset || '0');

    this.collectBodies();

    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this);

    // pause the physics loop while offscreen; resume (if unsettled) on return
    this.onscreen = true;
    this.viewObserver = new IntersectionObserver((entries) => {
      this.onscreen = entries.some((entry) => entry.isIntersecting);
      if (this.onscreen) {
        if (this.started && !this.everyoneAsleep()) this.run();
      } else {
        this.running = false;
        cancelAnimationFrame(this.rafId);
      }
    });
    this.viewObserver.observe(this);

    // text items size from their content, so re-measure once webfonts land
    document.fonts?.ready?.then(() => {
      if (this.isConnected) this.onResize();
    });

    if (this.skipAnimation) {
      this.settleInstantly();
    } else {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
            this.start();
          }
        },
        { threshold: 0.25 }
      );
      this.intersectionObserver.observe(this);
    }

    if (this.dragEnabled) {
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('click', this.onClickCapture, true);
    }

    if (window.Shopify && window.Shopify.designMode) {
      this.onBlockSelect = this.onBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.rafId);
    this.running = false;
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.viewObserver?.disconnect();
    mobileMedia.removeEventListener('change', this.onBreakpointChange);
    if (this.onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
  }

  collectBodies() {
    // items hidden by CSS (display:none → no offsetParent) sit out entirely
    const items = [...this.querySelectorAll('[data-gravity-item]')].filter(
      (el) => el.offsetParent !== null
    );
    const width = this.clientWidth || 1;
    const isMobile = mobileMedia.matches;
    this.isMobileLayout = isMobile;

    this.bodies = items.map((el, index) => {
      const rect = { w: el.offsetWidth || 1, h: el.offsetHeight || 1 };
      const isPill = el.dataset.shape === 'pill';
      const keepUpright = isPill || el.hasAttribute('data-keep-upright');
      // pills collide as capsules: a horizontal segment (halfLen) with a cap
      // radius, so wide bars (e.g. full-width buttons) stay flat instead of
      // acting like giant circles
      const radius = isPill ? Math.min(rect.w, rect.h) / 2 : Math.max(rect.w, rect.h) / 2;
      const halfLen = isPill ? Math.max(0, rect.w / 2 - radius) : 0;
      const maxTilt = keepUpright ? Math.min(0.35, (rect.h / Math.max(rect.w, 1)) * 1.5) : Infinity;

      // Pinned bodies hold a fixed x/y (and optional tilt) and act as
      // immovable obstacles; desktop and mobile each have their own pin
      const pinned = isMobile
        ? el.hasAttribute('data-pinned-mobile')
        : el.hasAttribute('data-pinned');
      const useMobilePin = isMobile && el.dataset.xMobile != null;
      const pinX = parseFloat(useMobilePin ? el.dataset.xMobile : el.dataset.x);
      const pinY = parseFloat(useMobilePin ? el.dataset.yMobile : el.dataset.y);
      const pinAngle =
        (parseFloat(useMobilePin ? el.dataset.rotationMobile : el.dataset.rotation) || 0) *
        (Math.PI / 180);

      // Preferred drop x: explicit data-x, otherwise spread evenly with jitter
      const spread = ((index + 0.5) / items.length) * width;
      const jitter = (this.pseudoRandom(index) - 0.5) * (width / items.length);
      const x = Number.isFinite(pinX) ? (pinX / 100) * width : spread + jitter;

      return {
        el,
        index,
        isPill,
        keepUpright,
        pinned,
        pinX: Number.isFinite(pinX) ? pinX : 50,
        pinY: Number.isFinite(pinY) ? pinY : 50,
        radius,
        halfLen,
        maxTilt,
        halfW: rect.w / 2,
        halfH: rect.h / 2,
        mass: Math.max((rect.w * rect.h) / 4, 1),
        x: pinned ? x : this.clampX(x, radius),
        y: pinned
          ? ((Number.isFinite(pinY) ? pinY : 50) / 100) * (this.clientHeight || 1)
          : -radius - this.pseudoRandom(index + 31) * 220,
        vx: 0,
        vy: 0,
        angle: pinned ? pinAngle : 0,
        va: 0,
        squash: 0,
        releaseAt: pinned ? 0 : index * this.stagger + this.pseudoRandom(index + 7) * 0.15,
        active: pinned,
        sleeping: pinned,
        stillFrames: 0,
        dragging: false,
      };
    });

    this.bodies.forEach((body) => this.render(body));
  }

  onBreakpointChange() {
    // crossed INTO an all-pinned mobile layout: stop simulating and hand
    // positioning to the static CSS rules
    if (this.isStaticLayout()) {
      this.enterStaticMode();
      return;
    }

    // crossed OUT of static mode: boot (or re-arm) the engine
    if (this.staticMode) {
      this.staticMode = false;
      if (!this.engineInited) {
        this.initEngine();
        return;
      }
    }

    this.collectBodies();
    if (this.skipAnimation) {
      this.settleInstantly();
    } else if (this.started) {
      // re-drop with the same choreography as the initial start
      this.clock = 0;
      this.wakeAll();
      this.run();
    }
  }

  enterStaticMode() {
    if (this.staticMode) return;
    this.staticMode = true;
    this.running = false;
    cancelAnimationFrame(this.rafId);
    // clear engine transforms so the static CSS placement isn't double-offset
    for (const body of this.bodies) body.el.style.transform = '';
    this.bodies = [];
    this.isMobileLayout = true;
  }

  // Deterministic jitter so the editor doesn't reshuffle on every re-render
  pseudoRandom(seed) {
    const value = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
    return value - Math.floor(value);
  }

  clampX(x, radius) {
    const width = this.clientWidth || 1;
    return Math.min(Math.max(x, radius), Math.max(width - radius, radius));
  }

  start() {
    this.started = true;
    // styling hook: pinned items play their entrance tilt off this class
    this.classList.add('is-started');
    this.clock = 0;
    this.wakeAll();
    this.run();
  }

  run() {
    if (this.running || !this.onscreen) return;
    this.running = true;
    this.lastTime = performance.now();
    const tick = (now) => {
      if (!this.running) return;
      const dt = Math.min((now - this.lastTime) / 1000, MAX_DT);
      this.lastTime = now;
      this.clock += dt;

      const sub = dt / SUBSTEPS;
      for (let i = 0; i < SUBSTEPS; i++) this.step(sub);
      this.bodies.forEach((body) => this.render(body));

      if (this.everyoneAsleep()) {
        this.running = false;
        return;
      }
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  everyoneAsleep() {
    return (
      !this.dragBody &&
      this.bodies.every((body) => body.active && body.sleeping)
    );
  }

  wakeAll() {
    this.bodies.forEach((body) => {
      if (body.pinned) return;
      body.sleeping = false;
      body.stillFrames = 0;
    });
    if (this.started) this.run();
  }

  step(dt) {
    const height = this.clientHeight + this.floorOffset;

    for (const body of this.bodies) {
      if (!body.active) {
        if (this.clock >= body.releaseAt) body.active = true;
        else continue;
      }
      if (body.sleeping || body.dragging) continue;
      body.px = body.x;
      body.py = body.y;
      body.vy += this.gravity * dt;
      // light air drag keeps the pile from jittering forever
      const drag = 1 - 0.06 * dt;
      body.vx *= drag;
      body.vy *= drag;
      body.va *= 1 - 1.5 * dt;
      body.x += body.vx * dt;
      body.y += body.vy * dt;
      body.va = Math.max(-4, Math.min(4, body.va));
      body.angle += body.va * dt;
      // impact squash recovers on a fast exponential
      body.squash *= Math.max(0, 1 - 10 * dt);
      if (body.squash < 0.003) body.squash = 0;
      if (body.keepUpright) body.angle = Math.max(-body.maxTilt, Math.min(body.maxTilt, body.angle));
    }

    // impulses once per substep; extra iterations only relax positions
    for (let iter = 0; iter < SOLVER_ITERATIONS; iter++) {
      this.solvePairs(iter === 0);
      this.solveBounds(height, iter === 0);
    }

    // sleeping bookkeeping: a body that has barely moved this step is
    // resting (on the floor or on the pile), regardless of transient velocity
    for (const body of this.bodies) {
      if (!body.active || body.dragging || body.sleeping) continue;
      const moved = Math.hypot(body.x - body.px, body.y - body.py);
      if (moved < SLEEP_SPEED * dt && Math.abs(body.va) < 0.25) {
        body.stillFrames++;
      } else {
        body.stillFrames = 0;
      }
      if (body.stillFrames > SLEEP_FRAMES) {
        body.sleeping = true;
        body.vx = 0;
        body.vy = 0;
        body.va = 0;
        body.squash = 0;
      }
    }
  }

  // closest point on a capsule body's core segment to (px, py)
  closestOnSegment(body, px, py) {
    const cos = Math.cos(body.angle);
    const sin = Math.sin(body.angle);
    const t = Math.max(
      -body.halfLen,
      Math.min(body.halfLen, (px - body.x) * cos + (py - body.y) * sin)
    );
    return [body.x + cos * t, body.y + sin * t];
  }

  solvePairs(applyImpulses) {
    const bodies = this.bodies;
    for (let i = 0; i < bodies.length; i++) {
      const a = bodies[i];
      if (!a.active) continue;
      for (let j = i + 1; j < bodies.length; j++) {
        const b = bodies[j];
        if (!b.active) continue;

        // contact points: capsules collide via the closest point on their
        // core segment (circles have halfLen 0, so this is a no-op for them)
        let ax = a.x;
        let ay = a.y;
        let bx = b.x;
        let by = b.y;
        if (a.halfLen > 0) [ax, ay] = this.closestOnSegment(a, bx, by);
        if (b.halfLen > 0) [bx, by] = this.closestOnSegment(b, ax, ay);
        if (a.halfLen > 0 && b.halfLen > 0) [ax, ay] = this.closestOnSegment(a, bx, by);

        const dx = bx - ax;
        const dy = by - ay;
        const minDist = a.radius + b.radius;
        const distSq = dx * dx + dy * dy;
        if (distSq >= minDist * minDist || distSq === 0) continue;

        const dist = Math.sqrt(distSq);
        const nx = dx / dist;
        const ny = dy / dist;
        const penetration = minDist - dist;

        // a dragged or fast body shoves resting bodies awake
        if (applyImpulses) {
          if (a.sleeping && (b.dragging || Math.hypot(b.vx, b.vy) > 120)) this.wake(a);
          if (b.sleeping && (a.dragging || Math.hypot(a.vx, a.vy) > 120)) this.wake(b);
        }

        const invMassA = a.dragging || a.sleeping ? 0 : 1 / a.mass;
        const invMassB = b.dragging || b.sleeping ? 0 : 1 / b.mass;
        const invMassSum = invMassA + invMassB;
        if (invMassSum === 0) continue;

        // positional correction
        const correction = (Math.max(penetration - 0.5, 0) * 0.6) / invMassSum;
        a.x -= nx * correction * invMassA;
        a.y -= ny * correction * invMassA;
        b.x += nx * correction * invMassB;
        b.y += ny * correction * invMassB;

        if (!applyImpulses) continue;

        // impulse
        const rvx = b.vx - a.vx;
        const rvy = b.vy - a.vy;
        const velAlongNormal = rvx * nx + rvy * ny;
        if (velAlongNormal > 0) continue;

        const impulse = (-(1 + this.restitution) * velAlongNormal) / invMassSum;
        a.vx -= impulse * nx * invMassA;
        a.vy -= impulse * ny * invMassA;
        b.vx += impulse * nx * invMassB;
        b.vy += impulse * ny * invMassB;

        // tangential friction + a bit of spin
        const tx = -ny;
        const ty = nx;
        const velAlongTangent = rvx * tx + rvy * ty;
        const frictionImpulse = (-velAlongTangent * 0.12) / invMassSum;
        a.vx -= frictionImpulse * tx * invMassA;
        a.vy -= frictionImpulse * ty * invMassA;
        b.vx += frictionImpulse * tx * invMassB;
        b.vy += frictionImpulse * ty * invMassB;
        a.va -= (velAlongTangent / a.radius) * 0.03;
        b.va += (velAlongTangent / b.radius) * 0.03;
        // touching bodies grind each other's spin down
        a.va *= 0.92;
        b.va *= 0.92;
      }
    }
  }

  wake(body) {
    if (body.pinned) return;
    body.sleeping = false;
    body.stillFrames = 0;
  }

  solveBounds(height, applyImpulses) {
    const width = this.clientWidth;
    for (const body of this.bodies) {
      if (!body.active || body.dragging || body.pinned) continue;

      // floor uses the visual half-height for pills so they sit flush
      const floorOffset = body.isPill ? body.halfH : body.radius;
      if (body.y + floorOffset > height) {
        body.y = height - floorOffset;
        if (applyImpulses) {
          if (body.vy > 0) {
            if (this.squashEnabled) {
              // squash-and-stretch: harder landings flatten more
              body.squash = Math.max(body.squash, Math.min(0.18, body.vy / 6000));
            }
            body.vy = -body.vy * this.restitution;
          }
          if (Math.abs(body.vy) < 20) body.vy = 0;
          body.vx *= 0.94;
          // roll: ease angular velocity toward vx / r
          body.va += (body.vx / body.radius - body.va) * 0.2;
          if (body.keepUpright) body.va -= body.angle * 0.15; // settle level
        }
      }

      const sideOffset = body.isPill ? body.halfW : body.radius;
      if (body.x - sideOffset < 0) {
        body.x = sideOffset;
        if (applyImpulses && body.vx < 0) body.vx = -body.vx * this.restitution;
      } else if (body.x + sideOffset > width) {
        body.x = width - sideOffset;
        if (applyImpulses && body.vx > 0) body.vx = -body.vx * this.restitution;
      }

      // ceiling only matters for thrown items
      if (body.y - body.radius < -height && body.vy < 0) {
        body.vy = 0;
      }
    }
  }

  render(body) {
    const x = body.x - body.halfW;
    const y = body.y - body.halfH;
    // slight vertical stretch while falling fast, flatten on impact
    let sx = 1;
    let sy = 1;
    if (this.squashEnabled && !body.sleeping && !body.dragging) {
      const stretch = Math.min(0.05, Math.max(body.vy, 0) / 12000);
      sx = 1 + body.squash - stretch;
      sy = 1 - body.squash + stretch;
    }
    body.el.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${sx.toFixed(3)}, ${sy.toFixed(3)}) rotate(${body.angle.toFixed(3)}rad)`;
  }

  /* ------------------------------------------------------------------ */
  /* Reduced motion: compute the resting pile off-screen, render once    */
  /* ------------------------------------------------------------------ */
  settleInstantly() {
    this.started = true;
    this.classList.add('is-started');
    this.bodies.forEach((body) => {
      if (body.pinned) return;
      body.active = true;
      body.y = -body.radius - this.pseudoRandom(body.index + 31) * 220;
    });
    const dt = 1 / 60;
    for (let i = 0; i < 480; i++) {
      this.clock += dt;
      this.step(dt);
      if (this.bodies.every((body) => body.sleeping)) break;
    }
    this.bodies.forEach((body) => this.render(body));
  }

  /* ------------------------------------------------------------------ */
  /* Resize                                                              */
  /* ------------------------------------------------------------------ */
  onResize() {
    // belt and braces: some browsers deliver the resize before the media
    // query change event — rebuild on breakpoint crossing from here too
    if (mobileMedia.matches !== this.isMobileLayout) {
      this.onBreakpointChange();
      return;
    }
    if (!this.bodies.length) return;
    for (const body of this.bodies) {
      body.halfW = (body.el.offsetWidth || 1) / 2;
      body.halfH = (body.el.offsetHeight || 1) / 2;
      body.radius = body.isPill
        ? Math.min(body.halfW, body.halfH)
        : Math.max(body.halfW, body.halfH);
      body.halfLen = body.isPill ? Math.max(0, body.halfW - body.radius) : 0;
      if (body.keepUpright) {
        body.maxTilt = Math.min(0.35, (body.halfH / Math.max(body.halfW, 0.5)) * 1.5);
      }
      body.mass = Math.max(body.halfW * body.halfH, 1);
      if (body.pinned) {
        body.x = (body.pinX / 100) * this.clientWidth;
        body.y = (body.pinY / 100) * this.clientHeight;
      } else if (body.active) {
        body.x = this.clampX(body.x, body.isPill ? body.halfW : body.radius);
        body.y = Math.min(body.y, this.clientHeight + this.floorOffset - body.halfH);
      }
    }
    if (this.skipAnimation) {
      this.settleInstantly();
    } else if (this.started) {
      this.wakeAll();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Dragging                                                            */
  /* ------------------------------------------------------------------ */
  onPointerDown(event) {
    const el = event.target.closest('[data-gravity-item]');
    if (!el || !this.started) return;
    const body = this.bodies.find((candidate) => candidate.el === el);
    if (!body || !body.active || body.pinned) return;

    this.pointerId = event.pointerId;
    this.pressX = event.clientX;
    this.pressY = event.clientY;
    this.pendingBody = body;
    this.suppressClick = false;
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (event.pointerId !== this.pointerId) return;

    if (!this.dragBody && this.pendingBody) {
      const moved = Math.hypot(event.clientX - this.pressX, event.clientY - this.pressY);
      if (moved > DRAG_THRESHOLD) {
        this.dragBody = this.pendingBody;
        this.dragBody.dragging = true;
        this.wake(this.dragBody);
        this.suppressClick = true;
        this.prevPointer = null;
        this.run();
      }
    }
    if (!this.dragBody) return;

    event.preventDefault();
    const bounds = this.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;
    const body = this.dragBody;

    if (this.prevPointer) {
      // velocity from pointer movement, for the throw on release
      body.vx = (x - this.prevPointer.x) * 60;
      body.vy = (y - this.prevPointer.y) * 60;
    }
    this.prevPointer = { x, y };
    body.x = this.clampX(x, body.isPill ? body.halfW : body.radius);
    body.y = Math.min(y, this.clientHeight + this.floorOffset - body.halfH);
    this.render(body);
  }

  onPointerUp(event) {
    if (event.pointerId !== this.pointerId) return;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);

    if (this.dragBody) {
      const body = this.dragBody;
      body.dragging = false;
      // cap throw speed
      const speed = Math.hypot(body.vx, body.vy);
      const maxSpeed = 1800;
      if (speed > maxSpeed) {
        body.vx = (body.vx / speed) * maxSpeed;
        body.vy = (body.vy / speed) * maxSpeed;
      }
      this.dragBody = null;
      this.wakeAll();
      setTimeout(() => (this.suppressClick = false), 0);
    }
    this.pendingBody = null;
    this.pointerId = undefined;
  }

  onClickCapture(event) {
    if (this.suppressClick) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Theme editor: pop the selected block so it is easy to spot          */
  /* ------------------------------------------------------------------ */
  onBlockSelect(event) {
    if (!this.contains(event.target)) return;
    const el = event.target.closest('[data-gravity-item]') || event.target.querySelector('[data-gravity-item]');
    if (!el) return;
    const body = this.bodies.find((candidate) => candidate.el === el);
    if (!body || body.pinned) return;
    if (!this.started) this.start();
    body.active = true;
    this.wake(body);
    body.vy = -650;
    body.vx = (this.pseudoRandom(body.index + 3) - 0.5) * 300;
    this.wakeAll();
  }
}

if (!customElements.get('gravity-section')) {
  customElements.define('gravity-section', GravitySection);
}
